// Web Bluetooth + ESC/POS receipt printing for 58mm thermal printers.
// On iOS use Bluefy browser (Safari does not implement Web Bluetooth).
// On Android use Chrome.

const ESC = 0x1B;
const GS  = 0x1D;
const LF  = 0x0A;

// BLE UART service UUIDs used by common 58mm thermal printers
const PRINTER_SERVICES = [
  '000018f0-0000-1000-8000-00805f9b34fb', // common Chinese BLE printer
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART service
  '0000ff00-0000-1000-8000-00805f9b34fb', // generic thermal UART
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Epson/Star BLE
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip BLE UART
];

let _device = null;
let _char   = null;

async function findWritableChar(server) {
  for (const uuid of PRINTER_SERVICES) {
    try {
      const svc  = await server.getPrimaryService(uuid);
      const chars = await svc.getCharacteristics();
      for (const c of chars) {
        if (c.properties.write || c.properties.writeWithoutResponse) return c;
      }
    } catch { /* service not on this printer, try next */ }
  }
  throw new Error('Printer not recognized — check that it is on and in range');
}

export async function connectPrinter() {
  if (_device?.gatt?.connected && _char) return _char;

  if (!navigator.bluetooth) {
    throw new Error(
      'Web Bluetooth not available.\n' +
      'Android: use Chrome.\n' +
      'iOS: install the Bluefy browser.'
    );
  }

  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: PRINTER_SERVICES,
  });

  const server = await device.gatt.connect();
  const char   = await findWritableChar(server);

  _device = device;
  _char   = char;

  device.addEventListener('gattserverdisconnected', () => {
    _device = null;
    _char   = null;
  });

  return char;
}

export function isPrinterConnected() {
  return !!(_device?.gatt?.connected && _char);
}

export function isBluetoothSupported() {
  return !!navigator.bluetooth;
}

// ── Chunked write ──────────────────────────────────────────────────────────

async function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

async function writeChunked(char, data, chunkSize = 100) {
  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.slice(i, i + chunkSize);
    try {
      if (char.properties.writeWithoutResponse) {
        await char.writeValueWithoutResponse(chunk);
      } else {
        await char.writeValue(chunk);
      }
    } catch {
      // MTU too small — retry in 20-byte packets
      for (let j = 0; j < chunk.length; j += 20) {
        const tiny = chunk.slice(j, j + 20);
        if (char.properties.writeWithoutResponse) {
          await char.writeValueWithoutResponse(tiny);
        } else {
          await char.writeValue(tiny);
        }
        await sleep(20);
      }
    }
    await sleep(15);
  }
}

// ── ESC/POS receipt builder ────────────────────────────────────────────────

function amountWords(n) {
  if (!n) return 'Zero';
  const one = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
    'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const ten = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  if (n < 20)        return one[n];
  if (n < 100)       return ten[~~(n/10)] + (n%10 ? ' '+one[n%10] : '');
  if (n < 1000)      return one[~~(n/100)] + ' Hundred' + (n%100 ? ' '+amountWords(n%100) : '');
  if (n < 100000)    return amountWords(~~(n/1000)) + ' Thousand' + (n%1000 ? ' '+amountWords(n%1000) : '');
  if (n < 10000000)  return amountWords(~~(n/100000)) + ' Lakh' + (n%100000 ? ' '+amountWords(n%100000) : '');
  return amountWords(~~(n/10000000)) + ' Crore' + (n%10000000 ? ' '+amountWords(n%10000000) : '');
}

function fmtDate(s) {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return s; }
}

function buildReceiptBytes(donation, committee) {
  const enc = new TextEncoder();
  const b   = [];

  const push   = (...v)  => b.push(...v);
  const text   = (s)     => b.push(...enc.encode(String(s ?? '')));
  const nl     = ()      => push(LF);
  const center = ()      => push(ESC, 0x61, 0x01);
  const left   = ()      => push(ESC, 0x61, 0x00);
  const bold   = (on)    => push(ESC, 0x45, on ? 1 : 0);
  const size   = (n)     => push(ESC, 0x21, n);
  const feed   = (n = 1) => push(ESC, 0x64, n);

  // 58mm paper at normal font = 32 chars per line
  const W    = 32;
  const dash = () => { text('-'.repeat(W)); nl(); };
  const eq   = () => { text('='.repeat(W)); nl(); };

  function padRow(l, r) {
    text(l + ' '.repeat(Math.max(1, W - l.length - r.length)) + r);
    nl();
  }

  function wrapLine(label, value) {
    const avail = W - label.length;
    const v = String(value ?? '');
    if (!v) { text(label.trimEnd()); nl(); return; }
    for (let i = 0; i < v.length; i += avail) {
      text((i === 0 ? label : ' '.repeat(label.length)) + v.slice(i, i + avail));
      nl();
    }
  }

  // ── Init ──
  push(ESC, 0x40);

  // ── Header ──
  center();
  bold(true);
  size(0x10); // double-height (still 32 chars wide)
  text(committee.name.slice(0, W)); nl();
  bold(false);
  size(0x00);
  text(committee.address); nl();
  text(`Ph: ${committee.phone}`); nl();
  dash();

  bold(true); text('DONATION RECEIPT'); nl(); bold(false);
  dash();

  // ── Meta row ──
  left();
  const dateTime = fmtDate(donation.date) + (donation.time ? ` ${donation.time}` : '');
  padRow(`Rcpt:${donation.receiptNo || ''}`, dateTime.slice(0, W - 5 - (donation.receiptNo || '').length));
  dash();

  // ── Donor details ──
  wrapLine('Name:  ', donation.donorName);
  text(`Phone: ${donation.phone || ''}`); nl();
  if (donation.address) wrapLine('Addr:  ', donation.address);
  text(`Mode:  ${donation.paymentMode || ''}`); nl();
  eq();

  // ── Amount ──
  center();
  text('AMOUNT RECEIVED'); nl();
  bold(true);
  size(0x30); // double-width + double-height → 16 chars/line
  text(('Rs.' + Number(donation.amount || 0).toLocaleString('en-IN')).slice(0, 16)); nl();
  bold(false);
  size(0x00);
  const words = amountWords(Number(donation.amount || 0)) + ' Rupees Only';
  for (let i = 0; i < words.length; i += W) { text(words.slice(i, i + W)); nl(); }
  eq();

  // ── QR code (ESC/POS native 2D barcode) ──
  center();
  const qrRaw = enc.encode(committee.communitylink);
  const qrLen = qrRaw.length + 3;
  const pL    = qrLen & 0xFF;
  const pH    = (qrLen >> 8) & 0xFF;
  push(GS, 0x28, 0x6B, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00); // model 2
  push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, 0x05);        // module size 5 dots
  push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, 0x31);        // error correction M
  push(GS, 0x28, 0x6B, pL, pH, 0x31, 0x50, 0x30, ...qrRaw);  // store data
  push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x51, 0x30);        // print
  text('Scan to join WhatsApp'); nl();
  dash();

  // ── Added by ──
  left();
  if (donation.receivedBy) { text(`Added by: ${donation.receivedBy}`); nl(); }
  dash();

  // ── Footer ──
  center();
  text(`Ganesh Chaturthi ${committee.year} - ${committee.domain}`); nl();
  text('Computer Generated Receipt'); nl();

  feed(5);
  push(GS, 0x56, 0x42, 0x00); // partial cut

  return new Uint8Array(b);
}

// ── Public API ────────────────────────────────────────────────────────────

export async function btPrintReceipt(donation, committee) {
  const char = await connectPrinter();
  const data = buildReceiptBytes(donation, committee);
  await writeChunked(char, data);
}
